import { useTranslation } from 'react-i18next'

import { Star, Zap } from 'lucide-react'

import { cn } from '@/lib/utils'

import { usePlans } from '@/hooks/usePlans'
import type { Plan } from '@/hooks/usePlans'

import { Card, CardContent } from '@/components/ui/card'

import { SmartButton } from '@/components/ui-custom/SmartButton'

import { PlanDuration } from '@/features/plans/types/plan.types'

import { useCurrentLocale } from '../../hooks/useCurrentLocale'
import { formatMoney } from '../../lib/money'
import ErrorMessage from '../errors/ErrorMessage'
import { Skeleton } from '../ui/skeleton'

interface PlanSelectorProps {
  selectedBranch: string | null
  selectedPlan: string | null
  onNext: () => void
  onBack?: () => void
  onSelect: (planId: string) => void
}

export function PlanSelector({ selectedBranch, selectedPlan, onNext, onBack, onSelect }: PlanSelectorProps) {
  const { t } = useTranslation(['auth', 'common'])
  const { data: plans, isLoading, error } = usePlans(selectedBranch)

  // Handle case when no branch is selected
  if (!selectedBranch) {
    return (
      <div className='mx-auto max-w-3xl space-y-8'>
        <Card className='rounded-xl border-0 px-6 py-8 shadow-lg'>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <div className='bg-muted mb-4 flex h-16 w-16 items-center justify-center rounded-full'>
              <Zap className='text-muted-foreground h-8 w-8' />
            </div>
            <h3 className='text-foreground mb-2 text-xl font-semibold'>
              {t('auth:planSelectorForm.errors.noBranchSelected', 'No Branch Selected')}
            </h3>
            <p className='text-muted-foreground mb-6 text-center text-sm'>
              {t(
                'auth:planSelectorForm.errors.noBranchSelectedDescription',
                'Please select a branch first to view available membership plans.'
              )}
            </p>
            <SmartButton variant='outline' onClick={onBack} className='mt-4'>
              {t('common:actions.backLink', 'Back')}
            </SmartButton>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className='mx-auto max-w-6xl animate-pulse space-y-8'>
        {/* Title Skeleton */}
        <div className='flex justify-center'>
          <Skeleton className='h-9 w-64 rounded-md' />
        </div>

        {/* Plans Grid Skeleton */}
        <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
          {[...Array(3)].map((_, i) => (
            <div key={i} className='relative h-full'>
              <Card className='h-full border border-gray-200'>
                <CardContent className='flex h-full flex-col space-y-5 p-5'>
                  {/* Header */}
                  <div className='flex items-start justify-between space-x-2'>
                    <Skeleton className='h-6 w-32 rounded-md' />
                    <Skeleton className='h-5 w-16 rounded-full' />
                  </div>

                  {/* Price */}
                  <div className='flex items-end gap-2'>
                    <Skeleton className='h-8 w-20 rounded-md' />
                    <Skeleton className='mb-1 h-4 w-24 rounded-md' />
                  </div>

                  {/* Description */}
                  <div className='flex-1 space-y-2.5'>
                    <Skeleton className='h-4 w-full rounded-md' />
                  </div>

                  {/* Button */}
                  <Skeleton className='mt-4 h-10 w-full rounded-md' />
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className='flex flex-col items-center gap-4'>
          <Skeleton className='h-12 w-full rounded-md sm:w-64' />
          <Skeleton className='h-5 w-16 rounded-md' />
        </div>
      </div>
    )
  }

  if (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : t('auth:planSelectorForm.errors.loadingFailed', 'Failed to load available plans')
    return <ErrorMessage message={errorMessage} />
  }

  // Handle empty plans array
  if (!plans || plans.length === 0) {
    return (
      <div className='mx-auto max-w-3xl space-y-8'>
        <Card className='rounded-xl border-0 px-6 py-8 shadow-lg'>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <div className='bg-muted mb-4 flex h-16 w-16 items-center justify-center rounded-full'>
              <Star className='text-muted-foreground h-8 w-8' />
            </div>
            <h3 className='text-foreground mb-2 text-xl font-semibold'>
              {t('auth:planSelectorForm.errors.noPlansAvailable', 'No Plans Available')}
            </h3>
            <p className='text-muted-foreground mb-6 text-center text-sm'>
              {t(
                'auth:planSelectorForm.errors.noPlansAvailableDescription',
                'There are no membership plans available for the selected branch. Please contact support for assistance.'
              )}
            </p>
            <SmartButton variant='outline' onClick={onBack} className='mt-4'>
              {t('common:actions.backLink', 'Back')}
            </SmartButton>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='mx-auto max-w-6xl space-y-8'>
      <h2 className='text-primary text-center text-3xl font-bold'>
        {t('auth:planSelectorForm.title', 'Choose Membership Plan')}
      </h2>

      <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
        {plans?.map((plan) => (
          <PlanCard key={plan.id} plan={plan} isSelected={plan.id === selectedPlan} onSelect={onSelect} />
        ))}
      </div>

      <div className='flex flex-col items-center gap-4'>
        <SmartButton
          variant='default'
          onClick={onNext}
          disabled={!selectedPlan}
          size='lg'
          className={cn(
            'h-12 w-full text-base font-semibold sm:w-64',
            !selectedPlan ? 'cursor-not-allowed bg-gray-300 text-gray-600' : null
          )}
        >
          {t('common:actions.continue', 'Continue')}
        </SmartButton>

        <SmartButton
          variant={'link'}
          onClick={onBack}
          type='button'
          className='text-sm text-gray-600 transition-all hover:text-gray-800'
        >
          {t('common:actions.backLink', 'Back')}
        </SmartButton>
      </div>
    </div>
  )
}

interface PlanCardProps {
  plan: Plan
  isSelected: boolean
  isPopular?: boolean
  onSelect: (id: string) => void
}

export function PlanCard({ plan, isSelected, isPopular = false, onSelect }: PlanCardProps) {
  const locale = useCurrentLocale()
  const { t } = useTranslation(['auth'])
  const planName = plan.translations?.[locale]?.name ?? plan.name
  const planDescription = plan.translations?.[locale]?.description ?? plan.description

  const isTrial = plan.plan_type === 'trial'
  const hasTrial = plan.has_trial && plan.trial_days && plan.trial_days > 0

  // Duration label mapping with static keys (best practice for i18next-scanner)
  const getDurationLabel = () => {
    if (plan.plan_type === 'lifetime') {
      return t('auth:planSelectorForm.duration.lifetime', 'Lifetime')
    }

    if (!plan.duration_unit || !plan.duration_value) {
      return t('auth:planSelectorForm.duration.notSpecified', 'Not Specified')
    }

    const durationKeys: Record<Exclude<PlanDuration, null>, string> = {
      day: t('auth:planSelectorForm.duration.day', '{{count}} Day', {
        count: plan.duration_value,
        defaultValue_one: '{{count}} Day',
        defaultValue_other: '{{count}} Days',
      }),
      week: t('auth:planSelectorForm.duration.week', '{{count}} Week', {
        count: plan.duration_value,
        defaultValue_one: '{{count}} Week',
        defaultValue_other: '{{count}} Weeks',
      }),
      month: t('auth:planSelectorForm.duration.month', '{{count}} Month', {
        count: plan.duration_value,
        defaultValue_one: '{{count}} Month',
        defaultValue_other: '{{count}} Months',
      }),
      year: t('auth:planSelectorForm.duration.year', '{{count}} Year', {
        count: plan.duration_value,
        defaultValue_one: '{{count}} Year',
        defaultValue_other: '{{count}} Years',
      }),
    }

    return durationKeys[plan.duration_unit] || `${plan.duration_value} ${plan.duration_unit}`
  }

  const durationLabel = getDurationLabel()

  if (!plan.is_public) {
    return null
  }

  return (
    <div className='relative h-full'>
      {/* Popular badge */}
      {isPopular && (
        <div className='absolute -top-3 left-1/2 z-10 flex -translate-x-1/2 transform items-center rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-1 text-xs font-bold text-white shadow-sm'>
          <Star className='mr-1 h-3 w-3' />
          {isSelected ? t('auth:planSelectorForm.current', 'current') : t('auth:planSelectorForm.popular', 'POPULAR')}
        </div>
      )}

      <Card
        onClick={() => onSelect(plan.id)}
        className={cn(
          'group h-full cursor-pointer border-2 transition-all duration-200',
          'hover:border-primary/30 py-2 hover:shadow-md',
          isSelected
            ? 'border-primary from-primary/3 to-primary/8 border-2 bg-gradient-to-br shadow-lg'
            : 'border-2 border-gray-200/50',
          isPopular && !isSelected && 'border-amber-100 bg-amber-50/30'
        )}
      >
        <CardContent className='flex h-full flex-col space-y-5 p-5'>
          {/* Header */}
          <div className='flex items-start justify-between'>
            <div>
              <h3 className='flex items-center gap-2 text-xl font-bold text-gray-900'>
                {planName}
                {isPopular && <Zap className='h-5 w-5 text-amber-500' />}
              </h3>
            </div>

            {isSelected && (
              <span className='inline-flex items-center rounded-full border border-blue-200 bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800'>
                {t('auth:planSelectorForm.badge.selected', 'Selected')}
              </span>
            )}
          </div>

          {/* Price */}
          <div className='flex items-end gap-2 whitespace-nowrap'>
            <span className='text-primary text-2xl font-bold'>{formatMoney(plan.price, plan.currency, locale)}</span>
            <span className='mb-1 text-sm text-gray-500'>/ {durationLabel}</span>
          </div>

          {/* Features / Description */}
          <div className='flex-1 space-y-2.5 text-sm text-gray-600'>
            {/* Plan description */}
            {planDescription && <p>{planDescription}</p>}

            {/* Class limit info */}
            <div className='inline-flex items-center space-x-2 rounded-sm bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700'>
              <span>
                {plan.class_limit === null || plan.class_limit === 0
                  ? t('auth:planSelectorForm.classLimit.unlimited', 'Unlimited classes')
                  : t('auth:planSelectorForm.classLimit.limited', '{{count}} classes', {
                      count: plan.class_limit,
                    })}
              </span>
            </div>

            {/* Trial info */}
            {hasTrial && (
              <div className='inline-flex items-center space-x-2 rounded-sm bg-green-50 px-3 py-1 text-xs font-semibold text-green-700'>
                <span>
                  {t('auth:planSelectorForm.trial.days', '{{count}} Day trial', {
                    count: plan.trial_days ?? 0,
                    defaultValue_one: '{{count}} Day trial',
                    defaultValue_other: '{{count}} Days trial',
                  })}
                </span>
                {plan.trial_class_limit && (
                  <>
                    <span className='text-green-700'>|</span>
                    <span>
                      {t('auth:planSelectorForm.trial.classLimit', 'Max {{count}} demo class', {
                        count: plan.trial_class_limit ?? 0,
                        defaultValue_one: 'Max {{count}} demo class',
                        defaultValue_other: 'Max {{count}} demo classes',
                      })}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* CTA Button */}
          <SmartButton
            variant={isSelected ? 'default' : 'outline'}
            size='sm'
            className={cn(
              'mt-auto w-full font-medium',
              isPopular &&
                !isSelected &&
                'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700'
            )}
          >
            {isSelected
              ? t('auth:planSelectorForm.btn.selected', 'Selected')
              : isTrial
                ? t('auth:planSelectorForm.btn.selectTrial', 'Start Free Trial')
                : t('auth:planSelectorForm.btn.select', 'Select Plan')}
          </SmartButton>
        </CardContent>
      </Card>
    </div>
  )
}
